import { useState, FormEvent } from 'react';

const FORM_ACTION_URL = 'https://readdy.ai/api/form/d78tjk9rcamcd36db98g';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [formData, setFormData] = useState({
    contactName: '',
    phoneNumber: '',
    brandName: '',
    challenge: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (formData.challenge.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('contactName', formData.contactName);
      formBody.append('phoneNumber', formData.phoneNumber);
      formBody.append('brandName', formData.brandName);
      formBody.append('challenge', formData.challenge);

      const response = await fetch(FORM_ACTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ contactName: '', phoneNumber: '', brandName: '', challenge: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2500);
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* 彈窗主體 */}
      <div className="relative bg-[#0A192F] border border-[#D4AF37]/30 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* 頂部裝飾光條 */}
        <div className="h-1 w-full rounded-t-2xl bg-gradient-to-r from-[#D4AF37] to-[#F0C040]"></div>

        {/* 標題列 */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            🛡️ 數位引擎啟動申請表
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* 表單內容 */}
        <form
          id="consultation-modal-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="px-8 py-7"
        >
          <div className="space-y-5">
            {/* 姓名 */}
            <div>
              <label className="block text-white/80 font-medium mb-2 text-sm">
                姓名 <span className="text-[#D4AF37]">*</span>
              </label>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                placeholder="您的姓名"
                required
              />
            </div>

            {/* 手機 */}
            <div>
              <label className="block text-white/80 font-medium mb-2 text-sm">
                手機 <span className="text-[#D4AF37]">*</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                placeholder="0912-345-678"
                required
              />
            </div>

            {/* 企業 / 品牌名稱 */}
            <div>
              <label className="block text-white/80 font-medium mb-2 text-sm">
                企業 / 品牌名稱 <span className="text-[#D4AF37]">*</span>
              </label>
              <input
                type="text"
                name="brandName"
                value={formData.brandName}
                onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                placeholder="例：台北精選 O2O 展間 / 品牌企業"
                required
              />
            </div>

            {/* 您目前的挑戰 */}
            <div>
              <label className="block text-white/80 font-medium mb-2 text-sm">
                您目前的挑戰 <span className="text-[#D4AF37]">*</span>
              </label>
              <textarea
                name="challenge"
                value={formData.challenge}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, challenge: e.target.value });
                  }
                }}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/35 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all resize-none text-sm"
                rows={5}
                placeholder="例如：業績難以追蹤、廣告費沒效果、名單管理混亂...請描述您目前最大的痛點。"
                maxLength={500}
                required
              />
              <div className="text-right text-xs text-white/35 mt-1">
                {formData.challenge.length}/500
              </div>
            </div>
          </div>

          {/* 提交狀態提示 */}
          {submitStatus === 'success' && (
            <div className="mt-5 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg">
              <p className="text-[#D4AF37] text-center font-medium text-sm">
                ✓ 申請已成功送出！顧問將於 24 小時內與您聯繫。
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-5 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-center font-medium text-sm">
                ✗ 送出失敗，請稍後再試或直接聯繫我們。
              </p>
            </div>
          )}

          {/* 送出按鈕 */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-6 bg-[#F5D020] hover:bg-[#FFE033] text-[#060F1E] py-4 rounded-lg text-base font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? '送出中...' : '🚀 預約我的免費系統展示'}
          </button>

          {/* 隱私聲明 */}
          <p className="text-white/40 text-xs text-center mt-4 leading-relaxed">
            🔒 您的資料將受到嚴格保密，僅用於診斷評估與顧問聯繫
          </p>
        </form>
      </div>
    </div>
  );
}
